/**
 * @author gyb(mocheer)
 * @email `mocheer@foxmail.com`  
 * @since 2016-11-4
 */
import lonlats from '@/utils/LonLats';
import Popup from '@/polyfill/Popup';
import VectorMarker from '@/feature/VectorMarker';
import Typhoon from "@/feature/Typhoon";
import Ellipse from '@/feature/Ellipse';
import "@/feature/Text";
import '@/feature/Heat';
import '@/feature/Balloon';
import Circle from '@/feature/Circle';
import River from '@/feature/River';
import MilitaryArrow from '@/feature/MilitaryArrow';
import 'overlapping-marker-spiderfier-leaflet/build/oms';
import topojson from '../utils/topojson';
// import { AntPath, antPath } from 'leaflet-ant-path';

//
let {
  isFunction,
  isArray,
  fmt,
  assign: $assign
} = T;
let {
  featureGroup: toGroup,
  marker: toMarker,
  polyline: toPolyline,
  polygon: toPolygon,
  circle: toCircle,
  icon: toIcon,
  divIcon: toDivIcon,
  imageOverlay: toImageOverlay
} = L;
let toArray = data => isArray(data) ? data : [data];
let toOptions = (options, data) => isFunction(options) ? options(data) : options || {};
let isFlat = L.LineUtil.isFlat;
/**
 * 要素管理的通用图层
 */
export default class FeatureLayer extends T.Class {
  dmap;
  map;
  info; // 预留，提供给菜单，用于绑定检索
  options; // 样式，图层配置
  /**
   * 
   * @param {*} options 
   */
  constructor(options) {
    super();

    this.initHook();
    // 台风模块需要设置预报机构
    this.config(options);
  }
  /**
   * 
   */
  initHook() {
    this._source = [];
    ['addPoints', 'addPolylines', 'addPolygons', 'addTyphoon', 'addAlarmLines'].forEach(name => {
      this.addFnHook(name, {
        precall: function () {
          let data = {
            fn: name,
            args: arguments
          };
          this._source.push(data)
          T.defer(() => {
            this.do('feature-add', data)
          })
        }
      })
    })
    this.addFnHook('clear', {
      precall: function () {
        this._source.length = 0;
        T.defer(() => {
          this.do('feature-clear');
        })
      }
    })
    this.addFnHook('suspend', {
      precall: function () {
        this.do('suspend');
      }
    })
  }
  /**
   * 连接，复制
   * @param {*} layer 
   */
  connect(layer) {
    let {
      options,
      _source
    } = this;
    layer.options = options;
    //
    _source.forEach(item => {
      layer[item.fn].apply(layer, item.args);
    })
    //
    this.on("feature-add", e => {
      layer[e.fn].apply(layer, e.args);
    }).on('feature-clear', () => {
      layer.clear();
    }).on('suspend', () => {
      layer.suspend();
    }).on('resume', () => {
      layer.resume();
    })
  }
  /**
   * 配置
   * @param options 
   */
  config(options) {
    this.options = options;
    this.layer = toGroup();
  }
  /**
   * 判断图元是否存在
   * @param {*} marker 
   */
  has(marker) {
    return this.layer.hasLayer(marker)
  }
  /**
   * 获取所有图层
   */
  getMarkers() {
    return this.layer.getLayers();
  }
  /**
   * 添加图元
   * @param {*} feature 
   */
  _add(feature) {
    this.layer.addLayer(feature);
  }
  /**
   * 添加图元，并绑定 tooltip、popup、text
   * @param feature 
   */
  add(feature, options, data) {
    if (feature) {
      feature.__data = data;
      this._add(feature);
      this._bindToolTip(feature, options, data);
      this._bindPopup(feature, options, data);
      this._bindText(feature, options, data);
      //
    }
  }
  /**
   * 添加点图元
   * @param data [{coordinates:[lon,lat] or [{lon,lat}] or [{lat,lng}]
   * @param style
   */
  addPoints(data, options) {
    return toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let feature = this.createPoint(item, featureOptions);
      this.add(feature, featureOptions, item);

      return feature;
    });
  }
  /**
   * 添加聚合点
   * @param {*} data 
   * @param {*} options 
   */
  addClusterPoints(data, options) {
    let group = L.markerClusterGroup(options && options.cluster);
    let {
      layer,
      _bindToolTip,
      _bindPopup
    } = this;
    layer.addLayer(group);
    let markers = toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let marker = this.createPoint(item, featureOptions);
      if (marker) {
        marker.__data = item;
        _bindToolTip(marker, featureOptions, item);
        _bindPopup(marker, featureOptions, item);
        group.addLayer(marker);
      }
      return marker;
    });
    markers.group = group
    return markers;
  }
  /**
   * 添加聚合点
   * @param {*} data 
   * @param {*} options 
   */
  addClusterPoints2(data, options) {
    let group = this.oms = this.oms || new L.OverlappingMarkerSpiderfier(this.map);
    let markers = this.addPoints(data, options)
    markers.forEach(marker => {
      group.addMarker(marker)
    })
    return group;
  }
  /**
   * 添加线图元
   * @param {*} data
   * @param {*} options
   */
  addPolylines(data, options) {
    return toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let marker = this.createPolyline(item, featureOptions);
      this.add(marker, featureOptions, item);
      marker.bringToBack(); //暂时底部 
      return marker;
    });
  }
  /**
   * 添加多边形图元
   * @param {*} data 
   * @param {*} options 
   */
  addPolygons(data, options) {
    return toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let marker = this.createPolygon(item, featureOptions);
      this.add(marker, featureOptions, item);
      marker.bringToBack(); //暂时底部
      return marker;
    });
  }
  /**
   * 添加圆形图元
   * @param {*} data 
   * @param {*} options 
   *  
   */
  addCircles(data, options) {
    return toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let marker = this.createCircle(item, featureOptions);
      this.add(marker, featureOptions, item);
      marker.bringToBack() // 暂时底部
      return marker;
    });
  }
  /**
   * 添加椭圆
   * @param {*} data 
   * @param {*} options 
   */
  addEllipses(data, options) {
    return toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let marker = this.createEllipse(item, featureOptions);
      this.add(marker, featureOptions, item);
      marker.bringToBack() // 暂时底部
      return marker;
    });
  }
  /**
   * 添加文本图元
   * @param {*} data 
   * @param {*} options 
   */
  addTexts(data, options) {
    return toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let marker = L.text(lonlats(item.coordinates || item), item.text || item.properties.text, featureOptions.feature || featureOptions);
      marker.__data = item;
      this.layer.addLayer(marker);
      return marker;
    })
  }
  /**
   * 添加河流
   * @param {*} data 
   * @param {*} options 
   */
  addRivers(data, options) {
    return toArray(data).map(item => {
      let {
        properties,
        coordinates
      } = item;
      let featureOptions = properties || toOptions(options, properties || item);
      let {
        feature
      } = featureOptions;

      let deep = (coordinates[0] && isArray(coordinates[0][0])) ? 2 : 1;
      let marker = new River(lonlats(coordinates, deep), feature);
      this.layer.addLayer(marker);
      return marker;
    })
  }
  /**
   * 添加图片图元(雷达图、云图)
   * @param {*} data 
   * @param {*} options 
   */
  addImage(data, options) {
    let marker = toImageOverlay(options.url, lonlats(data.coordinates, 1), options);
    this.layer.addLayer(marker);
    return marker;
  }
  /**
   * 添加军标箭头
   * @param {*} data 
   * @param {*} options 
   */
  addMilitaryArrows(data, options) {
    return toArray(data).map(item => {
      let featureOptions = toOptions(options, item);
      let {
        feature
      } = featureOptions;
      let {
        coordinates
      } = item, deep = 1;
      if (coordinates && coordinates[0] && isArray(coordinates[0][0])) {
        deep = 2;
      }
      let marker = new MilitaryArrow(lonlats(coordinates, deep), feature)
      this.add(marker, featureOptions, item);
      marker.bringToBack(); //暂时底部 
      return marker;
    });
  }
  /**
   * 添加台风 
   * @param {*} data 
   * @param {*} options 
   */
  addTyphoon(data, options) {
    let typhoons = this.typhoons || (this.typhoons = {});
    let feature = typhoons[data.tfbh];
    let featureOptions = toOptions(options || this.options);
    if (!feature) {
      feature = typhoons[data.tfbh] = this.createTyphoon(data, featureOptions)
      feature.addTo(this);
    } else { // 刷新
      feature.update(data);
    }
    return feature;
  }
  /**
   * 更新台风，预报机构勾选
   */
  updateTyphoon() {
    T.each(Object.keys(this.typhoons), id => {
      var typhoon = typhoons[id];
      typhoon.update();
    })
  }
  /**
   * 台风警戒线 
   * @deprecated 慎用，可能被废弃
   * @param data {
   *  g24: [[105, 0], [113, 4.5], [119, 11], [119, 18], [127, 22], [127, 34]],
   *  s24: { color: '#0000FF', opacity: 1, weight: 1, dashArray: '10,3', interactive: false },
   *  g48: [[105, 0], [120, 0], [132, 15], [132, 34]],
   *  s48: { color: '#FFFF00', opacity: 1, weight: 1, dashArray: '10,3', interactive: false } 
   * }
   */
  addAlarmLines(data) {
    if (!data) return;
    let {
      layer
    } = this;
    let createPolyline = (coor, options) => toPolyline(lonlats(coor, 1), options)
    let m48 = createPolyline(data.g48, data.s48);
    let m24 = createPolyline(data.g24, data.s24);
    layer.addLayer(m48).addLayer(m24);
    this.addTexts([{
      coordinates: data.g24[data.g24.length - 1],
      text: ['24', '小', '时', '警', '戒', '线'].join('\n')
    }], {
      feature: T.assign({
        font: '16px 新宋体',
        offset: [18, 60]
      }, data.s24)
    })
    this.addTexts([{
      coordinates: data.g48[data.g48.length - 1],
      text: ['48', '小', '时', '警', '戒', '线'].join('\n')
    }], {
      feature: T.assign({
        font: '16px 新宋体',
        offset: [18, 60]
      }, data.s48)
    })
  }
  /**
   * 添加热力图
   * @param data
   * @param options
   */
  addHeat(data, options) {
    options = toOptions(options);
    let marker = L.heat(lonlats(data, 1), options.feature);
    return this.add(marker, options, data), marker;
  }
  /**
   * todo:
   * 1、支持4326投影
   * @param data
   * @param options 
   */
  addVelocity(data, options) {
    return new Promise((resolve) => {
      T.require(['dmap/leaflet/plugins/velocityLayer'], ({
        default: createLayer
      }) => {
        let marker = createLayer(data, options);
        this._add(marker);
        resolve(marker);
      })
    })
  }
  /**
   * 
   * @param {*} data 
   * @param {*} options 
   */
  addWindy(data, options) {
    return new Promise(resolve => {
      T.require(['dmap/leaflet/plugins/windyLayer'], ({
        default: createWindyLayer
      }) => {
        options = T.assign({
          tempOverlay: true
        }, options);
        //
        let {
          windfarm
        } = this;
        if (windfarm && windfarm.options.tempOverlay === options.tempOverlay) {
          windfarm.loadWindy(data)
        } else {
          windfarm && windfarm.removeFrom(this.layer);
          windfarm = this.windfarm = createWindyLayer(options);
          this._add(windfarm);
          windfarm.loadWindy(data);
        }
        resolve(windfarm);
      })
    })
  }
  /**
   * 添加 shp 图层
   */
  addShp(url, options) {
    let layer = L.shapefile(url, options)
    this._add(layer);
    return layer;
  }
  /**
   * @see https://github.com/Flexberry/Leaflet-WFST
   * @see https://github.com/azgs/azgs-leaflet
   */
  addWFS(url, options) {
    T.require(['dmap/leaflet/plugins/wfst'], ({
      default: wfst
    }) => {
      options.url = url;
      new L.WFST(options, new L.Format.GeoJSON({
          crs: L.CRS.EPSG4326
        }))
        .addTo(this.map)
        .once('load', function () {
          this.map.fitBounds(wfst);
        });
    })
  }
  /**
   * 添加 geojson 图层  
   * @param {*} geojson 
   * @see https://leafletjs.com/reference-1.3.0.html#geojson
   */
  addGeoJSON(geojson, options) {
    let geometry = geojson.type === 'Feature' ? geojson.geometry : geojson;
    let coords = geometry ? geometry.coordinates : null;
    let type = geometry.type;
    if (!coords && !geometry) {
      return null;
    }
    options = isFunction(options) ? options : $assign({}, options, geometry.properties);

    switch (type) {
      case 'MultiPoint':
        geojson = coords.map(coordinates => {
          return {
            coordinates,
            properties: geometry.properties
          }
        })
      case 'Point':
        return this.addPoints(geojson, options);
      case 'LineString':
      case 'MultiLineString':
        // { coordinates: lonlats(coords, type === 'LineString' ? 0 : 1) }
        return this.addPolylines(geometry, options)
      case 'Polygon':
      case 'MultiPolygon':
        // { coordinates: lonlats(coords, type === 'Polygon' ? 1 : 2) }
        return this.addPolygons(geometry, options);
      case 'GeometryCollection':
        return geometry.geometries.map(data => this.addGeoJSON(data, options));
        // 拓展 geojson 的类型
      case 'Circle':
        return this.addCircles(geometry, options);
      case 'Ellipse':
        return this.addEllipses(geometry, options);
      case 'Text':
        return this.addTexts(geometry, options);
      case 'MilitaryArrow': //军标箭头
        return this.addMilitaryArrows(geometry, options);
      case 'FeatureCollection':
        return geometry.features.map(data => this.addGeoJSON(data, options));
    }
  }
  /**
   * 
   */
  addTopoJSON(json, options) {
    return this.addGeoJSON(topojson(json), options)
  }
  /**
   * 批量移除台风
   * @param {*} ids
   */
  removeTyphoons(ids) {
    let {
      typhoons
    } = this;
    if (typhoons) {
      ids = ids || Object.keys(typhoons);
      T.each(ids, id => {
        var typhoon = typhoons[id];
        if (typhoon) {
          typhoon.remove();
          delete typhoons[id];
        }
      })
    }
  }
  /**
   * 获取台风对象
   * @param {*} id
   */
  getTyphoon(id) {
    let {
      typhoons
    } = this;
    return id === void 0 ? typhoons : typhoons && typhoons[id];
  }
  /**
   * 创建台风对象
   * @param {*} data
   * @param {*} options 
   */
  createTyphoon(data, options) {
    return new Typhoon(data, options);
  }
  /**
   * 点图元工厂
   * @param {*} data
   * @param {*} options {feature:{icon,symbol,glowOptions,flowOptions}}
   */
  createPoint(data, options) {
    let {
      feature
    } = options;
    let {
      tag,
      src
    } = feature || {};
    //
    let latlng = lonlats(data.coordinates || data);
    if (latlng) {
      if (tag) {
        switch (tag) {
          case 'img':
            let anchor = feature.offset;
            if (anchor) {
              anchor = [-anchor[0], -anchor[1]];
            }
            feature.icon = toIcon($assign({
              iconUrl: src,
              iconAnchor: anchor,
              // iconSize: [38, 95],
              // popupAnchor: [-3, -76],
              // shadowUrl: 'shadow.png',
              // shadowSize: [68, 95],
              // shadowAnchor: [22, 94]
            }, feature.icon));
            break;
          case 'div':
            feature.icon = toDivIcon($assign({
              iconAnchor: anchor
            }, feature.icon))
            break;
        }
        return toMarker(latlng, feature);
      }

      return new VectorMarker(latlng, feature);
    }
  }
  /**
   * 线图元工厂
   * @param {*} data
   * @param {*} options 
   */
  createPolyline(data, options) {
    let {
      feature
    } = options;
    let {
      coordinates
    } = data, deep = 1;
    //todo
    if (coordinates && coordinates[0] && isArray(coordinates[0][0])) {
      deep = 2;
    }
    return toPolyline(lonlats(coordinates, deep), feature);
  }

  /**
   * 多边形图元工厂
   * @param {*} data
   * @param {*} options 
   */
  createPolygon(data, options) {
    let {
      feature
    } = options;
    let {
      coordinates
    } = data;
    let deep = (coordinates && coordinates[0] && isArray(coordinates[0][0])) ? 2 : 1;
    // 数据错误，容错下，标绘
    if (!isArray(coordinates[0])) {
      coordinates = [coordinates];
    }

    return toPolygon(lonlats(coordinates, deep), feature);
  }
  /**
   * 创建圆形图元
   * @param {*} data
   * @param {*} options 
   */
  createCircle(data, options) {
    if (options.feature && options.feature.isCircle) {
      return new Circle(lonlats(data.coordinates), options.feature);
    }
    return toCircle(lonlats(data.coordinates), options.feature);
  }
  /**
   * 创建椭圆图元
   * @param {*} data 
   * @param {*} options 
   */
  createEllipse(data, options) {
    return new Ellipse(lonlats(data.coordinates), options.feature);
  }
  /**
   * 绑定 tooltip ，浮出层，一般用于显示站名
   * @param marker 
   * @param options 
   * @param data 
   */
  _bindToolTip(marker, options, data) {
    if (options = options.tooltip) {
      let {
        text
      } = options;
      let tooltip = (data && typeof text == 'string') ? fmt(text, data.properties || data) : text;
      //
      if (tooltip) {
        // options.interactive = true;
        marker.bindTooltip(tooltip, options);
      }
    }
  }
  /**
   * 绑定 popup 弹出层，一般用于显示报图
   * @param marker 
   * @param options 
   */
  _bindPopup(marker, options) {
    options = options.popup;
    if (options) {
      let popup = new Popup(options).setContent(options.text);
      marker.bindPopup(popup, options);
      // marker.bindPopup(options.text, options);
    }
  }
  /**
   * 绑定静态文本，一般用于面和线上的文本显示
   * @param {*} feature
   * @param {*} options
   */
  _bindText(feature, options) {
    options = options.text;
    if (options) {
      feature.bindText(options.text, options);
    }
  }
  /**
   * @deprecated 慎用，这个方法可能会被弃用
   */
  updateTooltips() {
    this.map && this.map.eachLayer(layer => {
      let tooltip = layer.getTooltip();
      tooltip && tooltip.isOpen && tooltip.update()
    });
  }
  /**
   * 隐藏标注，常用于水雨情设置模块
   * @param hide
   */
  hideTooltip(hide) {
    if (hide === void 0) hide = true;
    this.layer.eachLayer(layer => {
      let tooltip = layer.getTooltip();
      if (tooltip) {
        layer.hideTooltip(hide);
      }
    })
  }
  /**
   * 清除
   */
  clear() {
    this.layer.clearLayers();
    this.typhoons = null;
    return this;
  }
  /**
   * 挂起
   */
  suspend() {
    let {
      map,
      layer
    } = this;
    map && map.removeLayer(layer);
    this.suspended = true;
  }
  /**
   * 重启
   */
  resume() {
    let {
      map,
      layer
    } = this;
    map && map.addLayer(layer);
  }
  /**
   * 添加
   * @param dmap 
   */
  addTo(dmap) {
    //
    this.dmap = dmap;
    this.map = dmap.map;
    //
    dmap.addLayer(this);
    return this;
  }
  /**
   * 移除
   */
  remove() {
    this.dmap.removeLayer(this.layer);
    this.dmap = null;
    this.map = null;
    return this;
  }
  /**
   * 
   */
  removeLayer(feature) {
    this.layer.removeLayer(feature)
  }
  /**
   * 
   * @param types 
   * @param fn 
   * @param ctx 
   */
  on(types, fn, ctx) {
    return this.layer.on(types, fn, ctx);
  }
  /**
   * 
   * @param types 
   * @param fn 
   * @param ctx 
   */
  once(types, fn, ctx) {
    return this.layer.once(types, fn, ctx);
  }
  /**
   * 
   * @param types 
   * @param fn 
   * @param ctx  
   */
  off(types, fn, ctx) {
    return this.layer.off(types, fn, ctx);
  }
  /**
   * 
   * @param {*} types 
   * @param {*} e 
   */
  do(types, e) {
    return this.layer.fire(types, e)
  }
  /**
   * 导出 GeoJson
   * @param precision
   */
  toGeoJSON(precision) {
    let {
      layer
    } = this;
    let jsons = [];

    layer.eachLayer(layer => {
      if (layer.toGeoJSON) {
        let json = layer.toGeoJSON(precision);
        let {
          geometry,
          properties
        } = json;
        let {
          __data: data,
          options
        } = layer;
        let tooltip = layer.getTooltip();
        let text = layer.getText();
        //
        geometry.properties = $assign(properties, geometry.properties, data && data.properties, {
          feature: options,
          tooltip: tooltip && tooltip.options,
          text: text && text.options
        });
        //
        geometry.properties.feature.className = ''; //暂时设置为空
        jsons.push(geometry);
      }
    });
    //
    return {
      geometries: jsons,
      type: 'GeometryCollection'
    };
    //
  }
}



// WEBPACK FOOTER //
// ./src/layer/FeatureLayer.js